import { useState, useEffect, useRef } from 'react';
import { Star, Plus, X, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  'Great Service', 'Professional', 'Fast Response', 'Excellent Quality',
  'Friendly Staff', 'Good Value', 'Highly Recommended', 'Trustworthy',
  'Expert Knowledge', 'Clean & Organized', 'On Time', 'Great Communication',
];

const ReviewSetup = ({ profile, setProfile }) => {
  const [googleUrl, setGoogleUrl] = useState('');
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (profile) {
      setGoogleUrl(profile.google_review_url || '');
      setCategories(profile.review_categories || []);
    }
  }, [profile]);

  const autoSave = async (data) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleUrlChange = (val) => {
    setGoogleUrl(val);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => autoSave({ google_review_url: val }), 1000);
  };

  const updateCategories = (newCats) => {
    setCategories(newCats);
    autoSave({ review_categories: newCats });
  };

  const addCategory = () => {
    const trimmed = newCategory.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    updateCategories([...categories, trimmed]);
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    updateCategories(categories.filter(c => c !== cat));
  };

  const addDefault = (cat) => {
    if (!categories.includes(cat)) {
      updateCategories([...categories, cat]);
    }
  };

  return (
    <div data-testid="review-setup" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-amber-400" /> AI Review Generator
        </h1>
        <p className="text-[#71717A] mt-1">Help your customers write Google reviews in seconds with AI</p>
      </div>

      {/* Google Review URL */}
      <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-1">
          <ExternalLink className="w-5 h-5 text-blue-400" />
          <h2 className="font-semibold text-lg text-white">Google Review Link</h2>
        </div>
        <p className="text-sm text-[#71717A] mb-4">Paste your Google Business review URL. Customers will be redirected here after generating their review.</p>
        <Input data-testid="google-review-url-input" value={googleUrl} onChange={(e) => handleUrlChange(e.target.value)}
          className="bg-[#1E1E1E] border-[#27272A] text-white" placeholder="https://g.page/r/your-business/review" />
        {saving && <p className="text-xs text-violet-400 mt-2">Saving...</p>}
      </div>

      {/* Review Categories */}
      <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-5 h-5 text-amber-400" />
          <h2 className="font-semibold text-lg text-white">Review Categories</h2>
        </div>
        <p className="text-sm text-[#71717A] mb-4">Define the categories your customers can select when generating a review</p>

        {/* Current categories */}
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(cat => (
            <span key={cat} data-testid={`cat-${cat.replace(/\s/g, '-')}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-sm">
              {cat}
              <button onClick={() => removeCategory(cat)} className="hover:text-red-400 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {categories.length === 0 && (
            <p className="text-sm text-[#525252] italic">No categories yet. Add some below or pick from suggestions.</p>
          )}
        </div>

        {/* Add custom */}
        <div className="flex gap-2 mb-4">
          <Input data-testid="new-category-input" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCategory()}
            className="bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Add custom category..." />
          <Button data-testid="add-category-btn" onClick={addCategory} disabled={!newCategory.trim()}
            className="bg-violet-500 hover:bg-violet-600 text-white shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {/* Suggested defaults */}
        <div>
          <p className="text-xs text-[#71717A] mb-2">Suggested categories:</p>
          <div className="flex flex-wrap gap-1.5">
            {DEFAULT_CATEGORIES.filter(c => !categories.includes(c)).map(cat => (
              <button key={cat} data-testid={`suggest-${cat.replace(/\s/g, '-')}`} onClick={() => addDefault(cat)}
                className="px-2.5 py-1 rounded-full text-xs bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white transition-all border border-transparent hover:border-[#27272A]">
                + {cat}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSetup;
